import {memo, useEffect, useMemo, useState} from 'react';
import ExplanationTooltip from '../reusableElementsComponents/ExplanationTooltip';
import AssessmentProgress, { buildAssessmentNameMap } from '../reusableElementsComponents/AssessmentProgress';
// import {useSelector} from 'react-redux';
import { t, localizeIndustryList, localizeProfileList } from '../../content';
import { useLocale } from '../../locale';
import {Select, Text, TextArea} from '../formComponents/Inputs';
import B2BApiService from "../../B2bApiService";
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import * as Constants from "../../constants";
import email from "../../assets/images/icons/email.svg";
import Button from "../reusableElementsComponents/Button";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Tooltip } from 'react-tooltip'
import Modal from '../reusableElementsComponents/Modal';
import WriteAccessGuard from '../reusableElementsComponents/WriteAccessGuard';
import { toast } from 'react-toastify';
import * as Utils from "../../Utilities";


function NewPositionGeneralEdit ({ data, updateData, poolHash, setPoolDataEditedFlag, formValidate, poolAssessments = [] }) {
    const { locale } = useLocale();

    let [jobIndustries, setJobIndustries] = useState([]);
    let [selectedJobProfiles, setSelectedJobProfiles] = useState([]);
    const departmentOptions = useMemo(() => {
        const list = localizeIndustryList(jobIndustries, locale);
        const selectedId = data?.industry?.id;
        if (selectedId == null || list.some((item) => String(item.id) === String(selectedId))) {
            return list;
        }
        const title = data.industry.name || data.industry.title || '';
        if (!title) {
            return list;
        }
        return [...list, { id: selectedId, title, value: selectedId }];
    }, [jobIndustries, locale, data?.industry]);
    const profileOptions = useMemo(
        () => localizeProfileList(selectedJobProfiles, locale),
        [selectedJobProfiles, locale]
    );
    const [deadline, setDeadline] = useState(data.deadline);
    const [selectedTargetProfile, setSelectedTargetProfile] = useState(data.target_profile ? data.target_profile : "");
    const [selectedDepartment, setSelectedDepartment] = useState(data.industry ? data.industry : "");

    const [newCandidateData, setNewCandidateData] = useState({
        first: '',
        last: '',
        email: ''
    });
    const [newCandidateDataErrors, setNewCandidateDataErrors] = useState({
        first: false,
        last: false,
        email: false
    });
    const newCandidateEmailErrorMessages = {
        first: t('validation.cannotBeEmpty'),
        last: t('validation.cannotBeEmpty'),
        email: t('validation.invalidEmail')
    };
    const [resendingInvite, setResendingInvite] = useState(false)
    const [visible, setVisible] = useState(false);
    const [selectedInviteUrl, setSelectedInviteUrl] = useState("")
    const [allAssessments, setAllAssessments] = useState([]);

    const copyUrlToClipboard = () => {
        navigator.clipboard.writeText(selectedInviteUrl);
        toast.success(t('toasts.inviteUrlCopied'));
        setVisible(false);
    }

    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    }, []);

    const handleTitleChange = (e) => {
        handleChange(e.target.value, 'title')
    }

    const handleDescriptionChange = (e) => {
        handleChange(e.target.value, 'description');
    }

    const handleDateChange = (date) => {
        setDeadline(date);
        handleChange(date, 'deadline');
    }

    const handleChange = (value, key) => {
        updateData({...data, [key]: value});
        setPoolDataEditedFlag(true);
    }

    const handleSelectTargetProfile = (e) => {
        const match = Utils.findProfileBySelectValue(profileOptions, e.target.value);
        if (!match) {
            return;
        }
        const targetProfile = {
            id: match.id,
            name: match.title
        };
        setSelectedTargetProfile(targetProfile);
        updateData({
            ...data,
            target_profile_id: match.id.toString(),
            target_profile: targetProfile
        });
        setPoolDataEditedFlag(true);
    }

    const handleSelectIndustry = (e) => {
        const match = Utils.findIndustryBySelectValue(jobIndustries, e.target.value);
        if (!match) {
            return;
        }
        setSelectedDepartment({
            id: match.id,
            name: match.title
        });
        setSelectedTargetProfile("");
        setSelectedJobProfiles([]);
        updateData({
            ...data,
            industry: {
                id: match.id,
                name: match.title
            },
            industry_id: match.id.toString(),
            target_profile_id: '',
            target_profile: null
        });
        setPoolDataEditedFlag(true);
        b2bServiceObj.LoadTargetProfilesByIndustry(match, setSelectedJobProfiles);
    }

    const removeMemberFromTeam = (member) => {
        let updatedMemberList = data?.members?.filter(m => m.email.toLowerCase() !== member.email.toLowerCase());
        handleChange(updatedMemberList, 'members');
    }

    const addNewTeamMember = () => {
        const newCandidateFirstnameError = newCandidateData.first.trim() === '';
        const newCandidateLastnameError = newCandidateData.last.trim() === '';
        const newCandidateEmailError = !Constants.VALID_EMAIL.test(newCandidateData.email);
        setNewCandidateDataErrors({
            first: newCandidateFirstnameError,
            last: newCandidateLastnameError,
            email: newCandidateEmailError
        });
        if (newCandidateFirstnameError || newCandidateLastnameError || newCandidateEmailError) {
            return;
        }
        if (data?.members?.some(m => m.email.toLowerCase() === newCandidateData.email.toLowerCase())) {
            toast.error(t('teams.toasts.memberExists'));
            return;
        } else {
            const newCandidate = {
                email: newCandidateData.email,
                first: newCandidateData.first,
                last: newCandidateData.last
            };
            let updatedMemberList = [...data?.members, newCandidate];
            handleChange(updatedMemberList, 'members');
            setNewCandidateData({ first: '', last: '', email: '' });
        }
    };

    const resendInvite = (member) => {
        if (resendingInvite) {
            return;
        }

        let resendInviteToast = toast.loading(t('toasts.sending'));
        setResendingInvite(true)

        let payload = { "user-id": member.id, "team-hash": poolHash };
        b2bServiceObj.ResendTeamInvite(payload, (res) => {
            if(res.status){
                toast.update(resendInviteToast, {
                    render: t('recruitment.toasts.inviteResent'),
                    type: "success",
                    isLoading: false,
                    autoClose: 1000,
                    closeButton: true,
                    hideProgressBar: true
                });
            }else{
                toast.update(resendInviteToast, {
                    render: t('recruitment.toasts.inviteResendError'),
                    type: "error",
                    isLoading: false,
                    autoClose: 1000,
                    closeButton: true,
                    hideProgressBar: true
                });
            }
            setResendingInvite(false);
        });
    };

    useEffect(() => {
        if (!jobIndustries.length) {
            b2bServiceObj.LoadIndustriesList(setJobIndustries);
        }
    }, [b2bServiceObj, jobIndustries.length]);

    useEffect(() => {
        if (!data?.industry?.id || !jobIndustries.length) {
            return;
        }
        setSelectedDepartment(data.industry);
        b2bServiceObj.LoadTargetProfilesByIndustry(data.industry, setSelectedJobProfiles);
    }, [b2bServiceObj, jobIndustries.length, data.industry]);

    useEffect(() => {
        setSelectedTargetProfile(data?.target_profile ? data.target_profile : "");
    }, [data.target_profile]);

    useEffect(() => {
        if (data?.deadline) {
            setDeadline(data.deadline);
        }
    }, [data?.deadline]);

    useEffect(() => {
        if (poolAssessments.length > 0) {
            b2bServiceObj.LoadAssessments(setAllAssessments);
        }
    }, [b2bServiceObj, poolAssessments.length]);

    const assessmentNames = useMemo(() => buildAssessmentNameMap(allAssessments, locale), [allAssessments, locale]);

    return (
        <>
            <div className="titleSection section">
                <div className="title">
                    <div className="field-label">{t('recruitment.form.positionName')}</div>
                    <Text
                        title=""
                        required={true}
                        fullWidth={true}
                        doValidation={formValidate}
                        errorContent={t('recruitment.form.positionTitleError')}
                        handleChange={handleTitleChange}
                        value={data?.title}
                    />
                </div>
                {data.deadline &&
                    <div className="date">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <div className="field-label">{t('recruitment.form.deadline')}</div>
                            <DatePicker
                                label=""
                                value={deadline}
                                onChange={date =>  handleDateChange(date)}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </LocalizationProvider>
                    </div>
                }
            </div>

            {data?.industry?.id &&
                <div className="departmentSection section">
                    <div className="department">
                        <div className="field-label">{t('recruitment.form.department')}</div>
                        {jobIndustries.length > 0 &&
                            <Select
                                id={'industry_id'}
                                label={t('recruitment.form.department')}
                                options={departmentOptions}
                                errorContent={t('validation.selectDepartment')}
                                handleChange={handleSelectIndustry}
                                option={selectedDepartment?.id ?? selectedDepartment ?? data?.industry?.id ?? ''}
                            />
                        }
                    </div>
                    <div className="targetProfile">
                        <div className="field-label">
                            {t('recruitment.form.targetProfile')}
                            <ExplanationTooltip>
                                <p>{t('recruitment.form.targetProfileExplanation')}</p>
                            </ExplanationTooltip>
                        </div>
                        {selectedDepartment && selectedJobProfiles.length > 0 &&
                            <Select
                            id={'target_profile'}
                            label={t('recruitment.form.targetProfile')}
                            options={profileOptions}
                            doValidation={formValidate}
                            errorContent={t('recruitment.form.targetProfileError')}
                            handleChange={handleSelectTargetProfile}
                            option={selectedTargetProfile?.id ?? ''}
                            />
                        }
                    </div>
                </div>
            }

            <div className="description">
                <div className="field-label">{t('recruitment.form.jobDescription')}</div>
                <TextArea
                    title=''
                    required={false}
                    fullWidth={true}
                    handleChange={handleDescriptionChange}
                    value={data.description}
                />
            </div>

            <div className="team-members-section section">
                <div className="field-label">{t('recruitment.candidateList')} <span className="mandatory">*</span></div>
                {(!data?.members || data?.members?.length === 0) && <div><br /><p className="m-0">{t('emptyStates.noCandidateFound')}</p></div>}
                <ul className='teamMembers'>
                    {data?.members && data?.members?.map((member, index) => {
                        return (
                            <li className="teamMember-list-item"
                                key={index}>
                                <div className={`teamMember-info ${member.invited ? "pending" : ""} ${member.invite_rejected ? "rejected" : ""}`}>
                                    <div className="title">
                                        <p className='name'>{member.first}{" "}{member.last}</p>
                                        <p className='email m-0'>{member.email}</p>
                                    </div>
                                    <div className="actions">
                                        {
                                            member?.invited && (
                                                <WriteAccessGuard nativeDisabled={false}>
                                                    <EmailIcon
                                                        className='icon email button-tooltip-element'
                                                        src={email} alt=""
                                                        data-tooltip-content={t('teams.resendInvite')}
                                                        onClick={() => {
                                                            resendInvite(member)
                                                        }}
                                                    />
                                                </WriteAccessGuard>)
                                        }
                                        {
                                            member?.invite_url && (
                                                <ContentCopyIcon
                                                    className='icon copy-url button-tooltip-element'
                                                    data-tooltip-content={t('teams.copyInviteUrl')}
                                                    onClick={() => {
                                                        setSelectedInviteUrl(member?.invite_url)
                                                        setVisible(true)
                                                    }}
                                                />)
                                        }
                                        {![Constants.USER_ROLE_TEAMLEAD, Constants.USER_ROLE_SPECIALIST].includes(Utils.currentUserRole()) && <WriteAccessGuard nativeDisabled={false}>
                                            <DeleteForeverIcon
                                                className='icon bin button-tooltip-element'
                                                data-tooltip-content={t('teams.removeMember')}
                                                onClick={() => {
                                                    removeMemberFromTeam(member)
                                                }}
                                            />
                                        </WriteAccessGuard>}
                                    </div>
                                </div>
                                <AssessmentProgress
                                    requiredAssessments={poolAssessments}
                                    completedAssessments={member.completed_assessments || []}
                                    assessmentNames={assessmentNames}
                                />
                            </li>
                        )
                    })}
                </ul>
                {![Constants.USER_ROLE_TEAMLEAD, Constants.USER_ROLE_SPECIALIST].includes(Utils.currentUserRole()) && <div>
                    <h5><strong>{t('recruitment.inviteNewCandidate')}</strong></h5>
                    <div className="inviteteamMember">
                    <TextField
                        className="text-field"
                        variant="standard"
                        id="candidate_first"
                        label={t('common.fields.firstName')}
                        name="candidate_first"
                        type="text"
                        value={newCandidateData.first || ''}
                        onChange={(e) => {
                            setNewCandidateData({
                                ...newCandidateData,
                                first: e.target.value
                            });
                            setNewCandidateDataErrors({
                                ...newCandidateDataErrors,
                                first: e.target.value.trim() === ''
                            });
                        }}
                        error={newCandidateDataErrors.first}
                        helperText={newCandidateDataErrors.first ? newCandidateEmailErrorMessages.first : ''}
                    />
                    <TextField
                        className="text-field"
                        variant="standard"
                        id="candidate_last"
                        label={t('common.fields.lastName')}
                        name="candidate_last"
                        type="text"
                        value={newCandidateData.last || ''}
                        onChange={(e) => {
                            setNewCandidateData({
                                ...newCandidateData,
                                last: e.target.value
                            });
                            setNewCandidateDataErrors({
                                ...newCandidateDataErrors,
                                last: e.target.value.trim() === ''
                            });
                        }}
                        error={newCandidateDataErrors.last}
                        helperText={newCandidateDataErrors.last ? newCandidateEmailErrorMessages.last : ''}
                    />
                    
                    <TextField
                        className="text-field"
                        variant="standard"
                        id="candidate_email"
                        label={t('common.fields.email')}
                        name="candidate_email"
                        type="text"
                        value={newCandidateData.email || ''}
                        onChange={(e) => {
                            setNewCandidateData({
                                ...newCandidateData,
                                email: e.target.value
                            });
                            setNewCandidateDataErrors({
                                ...newCandidateDataErrors,
                                email: !Constants.VALID_EMAIL.test(e.target.value)
                            });
                        }}
                        error={newCandidateDataErrors.email}
                        helperText={newCandidateDataErrors.email ? newCandidateEmailErrorMessages.email : ''}
                    />
                    <WriteAccessGuard>
                        <Button btnstyle={'mazhrBtn light-green invite-button'} onClick={addNewTeamMember}> <PersonAddAlt1Icon /> &nbsp; {t('common.actions.invite')}</Button>
                    </WriteAccessGuard>
                    </div>
                </div>}
                <Tooltip anchorSelect=".button-tooltip-element" />
                <Modal visible={visible} onHide={()=> setVisible(false)}
                    title={t('teams.inviteUrlTitle')}
                    text={selectedInviteUrl}
                    actionButtons={[{label: t('common.actions.copy'), onClick: copyUrlToClipboard}]}
                    cancel={()=> setVisible(false)} />
            </div>
        </>
    )
}

export default memo(NewPositionGeneralEdit)





